import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAccountSettings,
  updateAccountSettings,
  updateDomains,
  createToken,
  getTokens,
  deleteToken,
  getPIIConfiguration,
  updatePIIConfiguration,
  AccountSettings,
  PIIConfiguration,
  TokenDocument,
  inviteBulkUsers,
  UserInvite,
  InviteOptions,
  getDevTeams,
  getDevTeam,
  createDevTeam,
  updateDevTeam,
  deleteDevTeam,
  DevTeam,
  getIgnoreAccountIds,
  updateIgnoreAccountIds,
  IgnoreAccountIdsConfiguration,
  getMLSettings,
  updateMLSettings,
  MLSettings,
  getPACModelConfig,
  triggerPACModelRecalculation,
  PACModelConfig,
} from "../api/settings";

// Query keys
export const settingsKeys = {
  all: ["settings"] as const,
  account: () => [...settingsKeys.all, "account"] as const,
  tokens: (type: string) => [...settingsKeys.all, "tokens", type] as const,
  pii: () => [...settingsKeys.all, "pii"] as const,
  devTeams: {
    all: () => [...settingsKeys.all, "dev-teams"] as const,
    detail: (id: string) => [...settingsKeys.devTeams.all(), id] as const,
  },
  ignoreAccountIds: () => [...settingsKeys.all, "ignoreAccountIds"] as const,
  ml: () => [...settingsKeys.all, "ml"] as const,
  pacModelConfig: () => [...settingsKeys.all, "pacModelConfig"] as const,
};

// Account Settings Hooks
export const useAccountSettings = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.account(),
    queryFn: () => getAccountSettings(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateAccountSettings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<AccountSettings>) =>
      updateAccountSettings(settings, getAccessTokenSilently),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.account() });
    },
  });
};

export const useUpdateDomains = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, push }: { domain: string; push: boolean }) =>
      updateDomains(domain, push, getAccessTokenSilently),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.account() });
    },
  });
};

// API Tokens Hooks
export const useTokens = (type: string) => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.tokens(type),
    queryFn: () => getTokens(type, auth0),
    enabled: !!auth0 && !!type,
  });
};

export const useTokenActions = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: ({ type, name }: { type: string; name: string }) =>
      createToken(type, name, getAccessTokenSilently),
    onSuccess: (_, { type }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.tokens(type) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      deleteToken(id, getAccessTokenSilently),
    onSuccess: (_, { type }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.tokens(type) });
    },
  });

  return {
    createToken: createMutation.mutateAsync,
    deleteToken: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

// PII Redaction Hooks
export const usePIIConfiguration = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.pii(),
    queryFn: () => getPIIConfiguration(auth0),
    enabled: !!auth0,
  });
};

export const useUpdatePIIConfiguration = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: {
      isEnabled: boolean;
      selectedTypes: string[];
      excludedDomains: string[];
    }) => updatePIIConfiguration(update, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.pii() });
    },
  });
};

// Dev Teams Hooks
export const useDevTeams = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.devTeams.all(),
    queryFn: () => getDevTeams(auth0),
    enabled: !!auth0,
  });
};

export const useDevTeam = (id: string) => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.devTeams.detail(id),
    queryFn: () => getDevTeam(id, auth0),
    enabled: !!auth0 && !!id,
  });
};

export const useDevTeamActions = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (team: { name: string; description?: string }) =>
      createDevTeam(team, getAccessTokenSilently),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.devTeams.all() });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...update }: { id: string; name?: string; description?: string }) =>
      updateDevTeam(id, update, getAccessTokenSilently),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.devTeams.all() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.devTeams.detail(id) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDevTeam(id, getAccessTokenSilently),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.devTeams.all() });
    },
  });

  return {
    createDevTeam: createMutation.mutateAsync,
    updateDevTeam: updateMutation.mutateAsync,
    deleteDevTeam: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

// User Invitation Hook
export const useInviteBulkUsers = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ users, options }: { users: UserInvite[]; options?: InviteOptions }) =>
      inviteBulkUsers(users, options || {}, auth0),
    onSuccess: () => {
      // If there's a users list query to invalidate, add it here
      // For now, we don't invalidate any queries as this is just sending invites
    },
  });
};

// Ignore Account IDs Hooks
export const useIgnoreAccountIds = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.ignoreAccountIds(),
    queryFn: () => getIgnoreAccountIds(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateIgnoreAccountIds = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: {
      isEnabled?: boolean;
      accountIds?: string[];
    }) => updateIgnoreAccountIds(update, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.ignoreAccountIds() });
    },
  });
};

// ML Settings Hooks
export const useMLSettings = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.ml(),
    queryFn: () => getMLSettings(auth0),
    enabled: !!auth0,
  });
};

export const useUpdateMLSettings = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: {
      businessCategoryDaysWindow: { [businessCategoryKey: string]: number };
    }) => updateMLSettings(update, auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.ml() });
    },
  });
};

// PACModelConfig Hooks
export const usePACModelConfig = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: settingsKeys.pacModelConfig(),
    queryFn: () => getPACModelConfig(auth0),
    enabled: !!auth0,
  });
};

export const useTriggerPACModelRecalculation = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerPACModelRecalculation(auth0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.pacModelConfig() });
    },
  });
}; 